import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { PathOptions } from 'leaflet';
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';

import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { CC_CONSTANTS } from '../../assets/constants';
import { GenericComponent } from '../generic/generic.component';
import { SidenavService } from '../../../../../shared/services/sidenav.service';

import {
    colorAec1BlueLight,
    colorAec3Green,
    colorAec4Orange,
    colorAec7RedLight,
    colorAec8RedDark,
    colorBlack,
} from '../../../../../shared/all.constants';
import { LegendConfig } from '../../../../../shared/components/map/map.component';
import { convertNumber } from '../../../../../shared/all.helpers';
import {
    DefaultPolygonStyle,
    EmptyPolygonStyle,
    HatchImage,
    MapColors,
} from 'src/app/shared/components/map/map.constants';
import {
    LV_NETWORKS_UNDERGROUNDING_KPIS,
    LV_NETWORKS_UNDERGROUNDING_RATE_CONCESSION_CHART,
    LV_NETWORKS_UNDERGROUNDING_RATE_DEP_CHART,
    LV_NETWORKS_UNDERGROUNDING_RATE_MAP,
    LV_NETWORKS_UNDERGROUNDING_TYPE_PROP_CHART,
} from '../../assets/keys';

const KEYS = [
    LV_NETWORKS_UNDERGROUNDING_KPIS,
    LV_NETWORKS_UNDERGROUNDING_TYPE_PROP_CHART,
    LV_NETWORKS_UNDERGROUNDING_RATE_DEP_CHART,
    LV_NETWORKS_UNDERGROUNDING_RATE_CONCESSION_CHART,
    LV_NETWORKS_UNDERGROUNDING_RATE_MAP,
];

interface KpiData {
    //Minimum of a line
    organizationId?: string;
    year?: string;
    error?: boolean;

    totalLvNetworkLength?: number;
    totalLvNetworkLengthEvolution?: number;
    lvBurialRate?: number;
    lvBurialRateEvolution?: number;
    lvFeedersCount?: number;
    lvFeedersCountEvolution?: number;
}

interface ChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=75
    networkLengthLv?: number;
    //kpi_id=79
    undergroundNetworkRate?: number;
    //kpi_id=80
    overheadBareNetworkRate?: number;
    //kpi_id=81
    twistedOverheadNetworkRate?: number;
    error?: boolean;
}

interface ChartDataRate {
    organizationId?: string;
    year?: string;
    xDensityUsersPerKmNetwork?: number;
    yUndergroundRate?: number;
    error?: boolean;
    departmentCode?: string;
    departmentName?: string;
}

@UntilDestroy()
@Component({
    selector: 'app-lv-networks-undergrounding',
    templateUrl: './lv-networks-undergrounding.component.html',
    styleUrls: ['./lv-networks-undergrounding.component.scss'],
})
export class LvNetworksUndergroundingComponent extends GenericComponent {
    public texts = CC_CONSTANTS.lvNetworks.tabs.undergrounding.texts;

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(LV_NETWORKS_UNDERGROUNDING_KPIS),
    ) as Signal<KpiData[]>;

    public lengthChartSeries: ApexAxisChartSeries = [];
    public lengthChartData: Signal<ChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(LV_NETWORKS_UNDERGROUNDING_TYPE_PROP_CHART),
    ) as Signal<ChartData[]>;
    public lengthDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [0, 1, 2, 3],
        formatter: this.setDataLabelsFormatterBySerie({
            0: CC_CONSTANTS.lvNetworks.tabs.undergrounding.texts.lvNetworkTypeProportionChart
                .precision,
            1: CC_CONSTANTS.lvNetworks.tabs.undergrounding.texts.lvNetworkTypeProportionChart
                .precision,
            2: CC_CONSTANTS.lvNetworks.tabs.undergrounding.texts.lvNetworkTypeProportionChart
                .precision,
            3: CC_CONSTANTS.lvNetworks.tabs.undergrounding.texts.lvNetworkTypeProportionChart
                .precisionKm,
        }),
    };

    public lengthXaxisCategories: ApexXAxis['categories'];
    public lengthYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.lvNetworkTypeProportionChart.undergroundNetworkRateAxisTitle,
            min: 0,
            max: 100,
            title: {
                text: this.texts.lvNetworkTypeProportionChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.lvNetworkTypeProportionChart.undergroundNetworkRateAxisTitle,
            min: 0,
            max: 100,
            title: {
                text: this.texts.lvNetworkTypeProportionChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.lvNetworkTypeProportionChart.undergroundNetworkRateAxisTitle,
            min: 0,
            max: 100,
            title: {
                text: this.texts.lvNetworkTypeProportionChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.lvNetworkTypeProportionChart.networkLengthLvAxisTitle,
            min: 0,
            opposite: true,
            labels: {
                formatter: (value: number) => value.toString(),
            },
            title: {
                text: this.texts.lvNetworkTypeProportionChart.yaxisTitleRight,
            },
        },
    ];
    public lengthChartError: boolean = true;

    public rateChartSeries: ApexAxisChartSeries = [];
    public chartDataDep: Signal<ChartDataRate[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(LV_NETWORKS_UNDERGROUNDING_RATE_DEP_CHART),
    ) as Signal<ChartData[]>;
    public concessionChartData: Signal<ChartDataRate[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(LV_NETWORKS_UNDERGROUNDING_RATE_CONCESSION_CHART),
    ) as Signal<ChartData[]>;

    public rateYaxis: ApexYAxis[] = [
        {
            seriesName: 'Axis left',
            min: 0,
            max: 100,
            title: {
                text: this.texts.lvUndergroundRateChart.yaxisTitle,
            },
        },
    ];
    public rateDataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [] };
    public rateChartError: boolean = true;

    public geometries: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(LV_NETWORKS_UNDERGROUNDING_RATE_MAP),
    ) as Signal<any[]>;
    public legendConfig: LegendConfig[] = [
        {
            title: "Taux d'enfouissement BT",
            values: [
                { color: MapColors.red, value: '0 à 1%' },
                { color: MapColors.orange, value: '1 à 25%' },
                { color: MapColors.yellow, value: '25 à 50%' },
                { color: MapColors.paleGreen, value: '50 à 75%' },
                { color: MapColors.lightGreen, value: '75 à 99%' },
                { color: MapColors.green, value: '99 à 100%' },
                { color: `url(${HatchImage})`, value: 'Communes hors concession' },
            ],
        },
    ];
    public lvNetworkTypeProportionCustomTooltip: ApexTooltip['custom'];
    public lvUndergroundingRateCustomTooltip: ApexTooltip['custom'];

    constructor(
        private readonly store: Store,
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
    ) {
        //GenericComponent intialize sideNav, Tabs (if exists), title and subTitle
        super(route, sidenavService, store);

        effect(() => {
            const dataList = this.lengthChartData();
            this.lengthChartError = this.checkForError(dataList, [
                'xYear',
                'overheadBareNetworkRate',
                'undergroundNetworkRate',
                'twistedOverheadNetworkRate',
                'networkLengthLv',
            ]);
            if (!this.lengthChartError) {
                //auto value max yaxis
                const updatedYaxis = [...this.lengthYaxis];
                const maxNetworkLength = Math.max(...dataList.map((data) => data.networkLengthLv!));
                const roundedMax = Math.ceil(maxNetworkLength / 1000) * 1000;
                updatedYaxis[3] = {
                    ...updatedYaxis[3],
                    max: roundedMax,
                    labels: {
                        formatter: (value: number) => value.toLocaleString('fr-FR'),
                    },
                };
                this.lengthYaxis = updatedYaxis;
                this.lengthXaxisCategories = dataList.map((data) => data.xYear!);

                this.lengthChartSeries = [
                    {
                        name: this.texts.lvNetworkTypeProportionChart
                            .undergroundNetworkRateAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.undergroundNetworkRate!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.lvNetworkTypeProportionChart
                            .twistedOverheadNetworkRateAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.twistedOverheadNetworkRate!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.lvNetworkTypeProportionChart
                            .overheadBareNetworkRateAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.overheadBareNetworkRate!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.lvNetworkTypeProportionChart.networkLengthLvAxisTitle,
                        type: 'line',
                        data: dataList.map((data) => data.networkLengthLv!),
                        color: colorAec7RedLight,
                    },
                ];

                this.lengthDataLabels.enabledOnSeries = [0, 1, 2, 3];
            }
        });

        effect(() => {
            const dataListConcession = this.concessionChartData();
            const dataListDep = this.chartDataDep();
            const concessionError = this.checkForError(dataListConcession, [
                'xDensityUsersPerKmNetwork',
                'yUndergroundRate',
            ]);
            const depError = this.checkForError(dataListDep, [
                'xDensityUsersPerKmNetwork',
                'yUndergroundRate',
            ]);
            this.rateChartError = concessionError || depError;

            if (!this.rateChartError) {
                this.rateChartSeries = [
                    {
                        name: this.texts.lvUndergroundRateChart
                            .lvUndergroundRatedepartmentCodeAxisTitle,
                        type: 'scatter',
                        data: dataListDep.map((data) => ({
                            x: data.xDensityUsersPerKmNetwork,
                            y: data.yUndergroundRate,
                            departmentCode: data.departmentCode,
                            departmentName: data.departmentName,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.lvUndergroundRateChart
                            .lvUndergroundRateConcessionAxisTitle,
                        type: 'scatter',
                        data: dataListConcession.map((data) => ({
                            x: data.xDensityUsersPerKmNetwork,
                            y: data.yUndergroundRate,
                        })),
                        color: colorAec8RedDark,
                    },
                ];
                this.rateDataLabels = { enabled: true, enabledOnSeries: [] };
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.lvNetworkTypeProportionCustomTooltip = this.setChartCustomTooltip(
            this.texts.lvNetworkTypeProportionChart.toolTipPrecision,
            this.texts.lvNetworkTypeProportionChart.units,
        );

        this.lvUndergroundingRateCustomTooltip = this.setLvUndergroundRateCustomTooltip();
    }

    public getMapStyle(feature: GeoJSON.Feature | undefined): PathOptions {
        if (!feature?.properties || feature.properties['lvUndergroundingRate'] === null) {
            return EmptyPolygonStyle;
        }

        // Cities style
        let fillColor = MapColors.red;
        const lvUndergroundingRate = Number(feature.properties['lvUndergroundingRate']);
        if (lvUndergroundingRate > 99) {
            fillColor = MapColors.green;
        } else if (lvUndergroundingRate > 75) {
            fillColor = MapColors.lightGreen;
        } else if (lvUndergroundingRate > 50) {
            fillColor = MapColors.paleGreen;
        } else if (lvUndergroundingRate > 25) {
            fillColor = MapColors.yellow;
        } else if (lvUndergroundingRate > 1) {
            fillColor = MapColors.orange;
        }

        return {
            ...DefaultPolygonStyle,
            fillColor,
        };
    }

    public getTooltip(feature: GeoJSON.Feature): string {
        if (!feature.properties) {
            return '';
        }
        const { cityName, lvUndergroundingRate, isInConcession } = feature.properties;
        if (!isInConcession) {
            return `${cityName} - Hors concession`;
        }

        const valueStringified = convertNumber(lvUndergroundingRate);
        return `${cityName} - ${valueStringified} % d'enfouissement BT`;
    }

    public setChartCustomTooltip(
        precision: number[] = [],
        units: string[] = [],
    ): ApexTooltip['custom'] {
        return function ({ series, seriesIndex, dataPointIndex, w }) {
            const year = w.globals.categoryLabels[dataPointIndex];
            const hiddenSeries = new Set(w.globals.collapsedSeriesIndices);

            let tooltipHtml = `<div class="apexcharts-tooltip-title">${year}</div>`;

            (
                w.globals.initialSeries as Array<{ name: string; color?: string; data: any[] }>
            ).forEach((serie, idx) => {
                if (hiddenSeries.has(idx)) return;
                const serieName = serie.name;
                const serieColor = serie.color || colorBlack;
                const dataPoint = serie.data[dataPointIndex];

                if (dataPoint !== undefined) {
                    const value = precision[idx] !== null ? Number(dataPoint) : dataPoint;
                    const unit = units[idx] || '';

                    tooltipHtml += `
                            <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: ${idx}; display: flex; align-items: center;">
                                <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}; margin-right: 8px;"></span>
                                <div class="apexcharts-tooltip-text">
                                    <div class="apexcharts-tooltip-y-group">
                                        <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                                        <span class="apexcharts-tooltip-text-y-value">${convertNumber(value, precision[idx], false)} ${unit}</span>
                                    </div>
                                </div>
                            </div>
                        `;
                }
            });

            return tooltipHtml;
        };
    }

    public setLvUndergroundRateCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const precision = this.texts.lvUndergroundRateChart.precision;
            const serie = w.config.series[seriesIndex];

            const data = serie.data[dataPointIndex];
            const title =
                seriesIndex === 0
                    ? `${data.departmentCode} - ${data.departmentName}`
                    : 'Ma concession';

            const xaxisTitle = this.texts.lvUndergroundRateChart.tooltipXaxisTitle;
            const xValue = `${convertNumber(data.x, precision)} ${this.texts.lvUndergroundRateChart.tooltipXaxisUnit}`;

            const yaxisTitle = this.texts.lvUndergroundRateChart.tooltipYaxisTitle;
            const yValue = `${convertNumber(data.y, precision)} ${this.texts.lvUndergroundRateChart.tooltipYaxisUnit}`;

            const dataTooltips = [
                {
                    color: '',
                    name: xaxisTitle,
                    value: xValue,
                },
                {
                    color: '',
                    name: yaxisTitle,
                    value: yValue,
                },
            ];
            const html = this.setCustomTooltip([], title, dataTooltips);
            return html;
        };
    }
}
